import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';

export function useTenants() {
  return useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => admin.listTenants()
  });
}

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
}

/**
 * Every tenant by id, so a cross-tenant page can show the organisation's name
 * where it holds only the id. One shared query key with `useTenants`, so the
 * list is fetched once for the whole console and served from the cache after
 * that. `pending` is true only on the first load, which lets a cell show the
 * id quietly rather than flashing "Unknown".
 */
export function useTenantMap(): { map: Map<string, TenantSummary>; pending: boolean } {
  const { data, isPending } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => admin.listTenants(),
    staleTime: 5 * 60_000
  });
  const map = useMemo(() => {
    const m = new Map<string, TenantSummary>();
    for (const row of data?.data ?? []) {
      m.set(row.id, { id: row.id, name: row.name, slug: row.slug, isActive: row.is_active });
    }
    return m;
  }, [data]);
  return { map, pending: isPending };
}
